import React, { useState, useEffect, useCallback } from "react";
import {
  Brain, Database, Loader2, RefreshCw, Sparkles, History, Search,
  CheckCircle2, AlertCircle, Calendar, Zap, BookOpen, MessageSquare,
  Youtube, Twitter, BarChart3, Sliders, Send,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * BrainPanel — DoozyAd's autonomous marketing director (RAG + Gemini).
 *
 * Layout:
 *  - Left column: KB stats + Ingest button + Decisions audit log
 *  - Right column: "Ask the brain" form + latest decision card with reasoning
 */
export const BrainPanel = ({ authAxios }) => {
  const [kbStats, setKbStats] = useState(null);
  const [decisions, setDecisions] = useState([]);
  const [intent, setIntent] = useState("Plan tomorrow's organic Instagram post that drives weekend bookings");
  const [targetDate, setTargetDate] = useState("");
  const [latest, setLatest] = useState(null);
  const [busyAsk, setBusyAsk] = useState(false);
  const [busyIngest, setBusyIngest] = useState(false);
  const [error, setError] = useState("");

  const loadStats = useCallback(async () => {
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/doozyad/brain/kb-stats`);
      setKbStats(r.data);
    } catch (e) { setError(e?.response?.data?.detail || "Failed to load KB stats"); }
  }, [authAxios]);

  const loadDecisions = useCallback(async () => {
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/doozyad/brain/decisions?limit=10`);
      setDecisions(r.data?.decisions || []);
    } catch { /* ignore */ }
  }, [authAxios]);

  useEffect(() => { loadStats(); loadDecisions(); }, [loadStats, loadDecisions]);

  const ingest = async () => {
    setBusyIngest(true); setError("");
    try {
      await authAxios.post(`${API_URL}/api/admin/doozyad/brain/ingest`, null, { timeout: 90000 });
      await loadStats();
    } catch (e) { setError(e?.response?.data?.detail || "Ingest failed"); }
    setBusyIngest(false);
  };

  const ask = async () => {
    if (!intent.trim()) return;
    setBusyAsk(true); setError(""); setLatest(null);
    try {
      const r = await authAxios.post(
        `${API_URL}/api/admin/doozyad/brain/ask`,
        { intent: intent.trim(), target_date: targetDate || null },
        { timeout: 120000 },
      );
      setLatest(r.data);
      await loadDecisions();
    } catch (e) { setError(e?.response?.data?.detail || "Brain call failed"); }
    setBusyAsk(false);
  };

  return (
    <div className="space-y-5" data-testid="doozyad-brain-panel">
      <div className="bg-gradient-to-br from-indigo-50 via-violet-50 to-fuchsia-50 border border-violet-200 rounded-2xl p-5">
        <h3 className="text-base font-bold text-[#1E1B4B] flex items-center gap-2 mb-1">
          <Brain size={20} className="text-violet-600" /> DoozyAd Brain
        </h3>
        <p className="text-xs text-[#64748B]">
          Autonomous marketing director. RAG-augmented (BM25 over your bookings, reviews, past posts,
          festivals, brand rules). Decides theme + copy brief + budget + post time with cited evidence.
          Founder rules locked: posts always queued for day-prior approval · paid boost auto-activates only ≤ ₹200/day.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* ── Left column ── */}
        <div className="space-y-4">
          <KbStatsCard stats={kbStats} onIngest={ingest} busy={busyIngest} onRefresh={loadStats} />
          <AutonomyConfigCard authAxios={authAxios} />
          <ChannelsStatusCard authAxios={authAxios} />
        </div>

        {/* ── Right column ── */}
        <div className="space-y-4">
          <AskBrainCard
            intent={intent} setIntent={setIntent}
            targetDate={targetDate} setTargetDate={setTargetDate}
            onAsk={ask} busy={busyAsk}
          />
          <PlanTomorrowCard authAxios={authAxios} />
          <AskAboutCard authAxios={authAxios} />
          {latest && <DecisionCard decision={latest} highlight />}
          <DecisionsLog decisions={decisions} onRefresh={loadDecisions} />
        </div>
      </div>

      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="doozyad-brain-error">
          {error}
        </div>
      )}
    </div>
  );
};

const KbStatsCard = ({ stats, onIngest, busy, onRefresh }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="doozyad-brain-kb-stats">
    <div className="flex items-center justify-between mb-3">
      <h4 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-1.5">
        <Database size={14} className="text-violet-600" /> Knowledge base
      </h4>
      <button onClick={onRefresh} className="text-[10px] text-[#64748B] hover:text-[#1E1B4B] flex items-center gap-1">
        <RefreshCw size={10} /> refresh
      </button>
    </div>
    {!stats ? (
      <div className="flex items-center gap-2 text-xs text-[#64748B]">
        <Loader2 size={12} className="animate-spin" /> Loading…
      </div>
    ) : (
      <>
        <div className="text-3xl font-bold text-violet-700 mb-0.5">{(stats.total_chunks || 0).toLocaleString("en-IN")}</div>
        <div className="text-[10px] text-[#64748B] uppercase tracking-wider mb-3">chunks indexed (max {stats.max_capacity})</div>
        <div className="space-y-1 mb-3">
          {Object.entries(stats.by_source || {}).sort((a, b) => b[1] - a[1]).map(([src, n]) => (
            <div key={src} className="flex items-center text-[11px]">
              <span className="text-[#64748B] capitalize w-32">{src.replace(/_/g, " ")}</span>
              <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden ml-2">
                <div className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500"
                  style={{ width: `${Math.min(100, (n / Math.max(1, stats.total_chunks)) * 100)}%` }} />
              </div>
              <span className="text-[#1E1B4B] font-semibold ml-2 tabular-nums">{n}</span>
            </div>
          ))}
        </div>
        {stats.last_ingested_at && (
          <div className="text-[10px] text-[#94a3b8] mb-3">
            Last ingested {new Date(stats.last_ingested_at).toLocaleString("en-IN")}
          </div>
        )}
        <button onClick={onIngest} disabled={busy}
          className="w-full px-3 py-1.5 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-xs font-semibold rounded-lg disabled:opacity-50 flex items-center justify-center gap-1.5"
          data-testid="doozyad-brain-ingest-btn">
          {busy ? <Loader2 size={11} className="animate-spin" /> : <BookOpen size={11} />}
          {busy ? "Ingesting (~30s)…" : "Ingest now"}
        </button>
      </>
    )}
  </div>
);

const AskBrainCard = ({ intent, setIntent, targetDate, setTargetDate, onAsk, busy }) => (
  <div className="bg-white border border-violet-200 rounded-xl p-4" data-testid="doozyad-brain-ask">
    <h4 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-1.5 mb-3">
      <Sparkles size={14} className="text-violet-600" /> Ask the Brain
    </h4>
    <label className="block text-[10px] font-semibold text-[#64748B] mb-1">Intent</label>
    <textarea value={intent} onChange={(e) => setIntent(e.target.value)} rows={3}
      placeholder="e.g. Plan tomorrow's Reel for the Mother's Day weekend audience"
      className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg outline-none focus:border-violet-400"
      data-testid="doozyad-brain-intent" />
    <label className="block text-[10px] font-semibold text-[#64748B] mb-1 mt-3">Target post date (optional · defaults to tomorrow)</label>
    <input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)}
      className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg outline-none focus:border-violet-400"
      data-testid="doozyad-brain-target-date" />
    <button onClick={onAsk} disabled={busy || !intent.trim()}
      className="mt-3 w-full px-3 py-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-xs font-bold rounded-lg disabled:opacity-50 flex items-center justify-center gap-1.5"
      data-testid="doozyad-brain-ask-btn">
      {busy ? <><Loader2 size={12} className="animate-spin" /> Thinking with RAG context…</> : <><Brain size={12} /> Decide</>}
    </button>
  </div>
);

const DecisionCard = ({ decision, highlight }) => {
  const conf = decision.confidence || 0;
  const confColor = conf >= 0.8 ? "emerald" : conf >= 0.5 ? "amber" : "red";
  const autoBoostable = decision.auto_boost_eligible;
  return (
    <div className={`border rounded-xl p-4 ${highlight ? "bg-gradient-to-br from-violet-50 to-fuchsia-50 border-violet-300" : "bg-white border-gray-200"}`}
      data-testid={`doozyad-brain-decision-${decision.decision_id || "latest"}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="text-xs font-bold text-violet-700 uppercase tracking-wider">{decision.theme_id}</div>
          <div className="text-[10px] text-[#64748B] mt-0.5">
            <Calendar size={10} className="inline mr-0.5" /> {decision.target_date} @ {decision.post_time_ist} IST
          </div>
        </div>
        <div className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-${confColor}-100 text-${confColor}-700`}>
          {Math.round(conf * 100)}% conf
        </div>
      </div>
      <p className="text-xs text-[#1E1B4B] mb-2">{decision.copy_brief}</p>
      <div className="flex items-center gap-2 text-[10px] text-[#64748B] mb-2 flex-wrap">
        <span className="px-1.5 py-0.5 bg-pink-50 text-pink-700 rounded font-semibold">
          {(decision.channel_mix || []).join(" + ")}
        </span>
        <span className={`px-1.5 py-0.5 rounded font-semibold ${decision.budget_inr === 0 ? "bg-gray-100 text-gray-600" : autoBoostable ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
          {decision.budget_inr === 0 ? "Organic only" : `₹${decision.budget_inr}/day · ${autoBoostable ? "auto-boost ✅" : "ask founder ⚠"}`}
        </span>
      </div>
      {decision.reasoning && (
        <div className="bg-white/70 border border-violet-100 rounded p-2 text-[11px] text-[#1E1B4B] mb-2">
          <span className="font-semibold text-violet-700">Reasoning · </span>{decision.reasoning}
        </div>
      )}
      {decision.retrieved_context && decision.retrieved_context.length > 0 && (
        <details className="text-[10px] text-[#64748B]">
          <summary className="cursor-pointer hover:text-violet-700 font-semibold flex items-center gap-1">
            <Search size={10} /> {decision.retrieved_context.length} context chunks used
          </summary>
          <div className="mt-1 space-y-1 pl-3 border-l-2 border-violet-100 max-h-44 overflow-y-auto">
            {decision.retrieved_context.map((c, i) => (
              <div key={`${c.source_type}-${c.source_id}-${i}`} className="text-[10px]">
                <span className="font-semibold text-violet-600">[{i + 1}]</span>{" "}
                <span className="text-[#1E1B4B]">{c.source_type}/{(c.source_id || "").slice(0, 20)}</span>{" "}
                <span className="text-[#94a3b8]">(score {c.score})</span>
                <div className="text-[#64748B] truncate">{c.text}</div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

const DecisionsLog = ({ decisions, onRefresh }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="doozyad-brain-decisions-log">
    <div className="flex items-center justify-between mb-3">
      <h4 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-1.5">
        <History size={14} className="text-violet-600" /> Recent decisions
      </h4>
      <button onClick={onRefresh} className="text-[10px] text-[#64748B] hover:text-[#1E1B4B] flex items-center gap-1">
        <RefreshCw size={10} /> refresh
      </button>
    </div>
    {decisions.length === 0 ? (
      <div className="text-center py-6">
        <AlertCircle size={20} className="text-gray-300 mx-auto mb-1" />
        <p className="text-xs text-[#64748B]">No decisions yet — ask the brain to plan something.</p>
      </div>
    ) : (
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {decisions.map((d) => (
          <div key={d.decision_id} className="border border-gray-100 rounded p-2 hover:bg-gray-50 cursor-default">
            <div className="flex items-center gap-2 text-[11px]">
              <span className="font-semibold text-violet-700">{d.theme_id}</span>
              <span className="text-[#64748B]">→ {d.target_date}</span>
              <span className="ml-auto text-[10px] text-[#94a3b8]">{d.confidence ? Math.round(d.confidence * 100) + "%" : ""}</span>
            </div>
            <div className="text-[10px] text-[#64748B] truncate mt-0.5">{d.copy_brief || d.intent}</div>
            <div className="text-[9px] text-[#94a3b8] mt-0.5">
              {d.decided_at ? new Date(d.decided_at).toLocaleString("en-IN") : ""}
              {d.budget_inr > 0 && <span className="ml-2">· ₹{d.budget_inr}/day</span>}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

// keep imports referenced for potential future expansion
void Zap;
void CheckCircle2;


// ──────────────────────────────────────────────────────────────────────
// iter149 · Autonomy config (confidence threshold + auto-plan toggle)
// ──────────────────────────────────────────────────────────────────────
const AutonomyConfigCard = ({ authAxios }) => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/doozyad/brain/config`);
      setCfg(r.data);
    } catch { /* ignore */ }
  }, [authAxios]);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setSaving(true); setMsg("");
    try {
      const r = await authAxios.post(`${API_URL}/api/admin/doozyad/brain/config`, patch);
      setCfg(r.data);
      setMsg("Saved");
      setTimeout(() => setMsg(""), 1500);
    } catch (e) { setMsg(e?.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  if (!cfg) return null;
  const thr = cfg.auto_schedule_confidence_threshold || 0.85;

  return (
    <div className="bg-white border border-violet-200 rounded-xl p-4" data-testid="doozyad-brain-autonomy-config">
      <h4 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-1.5 mb-2">
        <Sliders size={14} className="text-violet-600" /> Autonomy
      </h4>
      <div className="mb-3">
        <div className="flex items-center justify-between text-[11px] mb-1">
          <span className="font-semibold text-[#1E1B4B]">Auto-schedule confidence threshold</span>
          <span className="font-bold text-violet-700 tabular-nums">{Math.round(thr * 100)}%</span>
        </div>
        <input
          type="range" min={50} max={100} step={1}
          value={Math.round(thr * 100)}
          onChange={(e) => setCfg({ ...cfg, auto_schedule_confidence_threshold: Number(e.target.value) / 100 })}
          onMouseUp={(e) => save({ auto_schedule_confidence_threshold: Number(e.target.value) / 100 })}
          onTouchEnd={(e) => save({ auto_schedule_confidence_threshold: Number(e.target.value) / 100 })}
          className="w-full accent-violet-600"
          disabled={saving}
          data-testid="doozyad-brain-threshold-slider"
        />
        <div className="flex justify-between text-[9px] text-[#94a3b8] mt-0.5">
          <span>50% · more auto-publish</span>
          <span>100% · always ask</span>
        </div>
        <p className="text-[10px] text-[#64748B] mt-1.5">
          At or above this confidence, the brain auto-schedules tomorrow's post. Below it → sits in Pending for your approval.
        </p>
      </div>

      <label className="flex items-center gap-2 cursor-pointer text-[11px]" data-testid="doozyad-brain-autoplan-toggle">
        <input type="checkbox" checked={!!cfg.auto_plan_enabled}
          onChange={(e) => save({ auto_plan_enabled: e.target.checked })}
          disabled={saving}
          className="accent-violet-600" />
        <span className="font-semibold text-[#1E1B4B]">
          Auto-plan tomorrow's post daily at{" "}
          <select value={cfg.auto_plan_time_ist_hour || 18}
            onChange={(e) => save({ auto_plan_time_ist_hour: Number(e.target.value) })}
            disabled={saving || !cfg.auto_plan_enabled}
            className="text-xs px-1 py-0.5 border border-gray-200 rounded"
            data-testid="doozyad-brain-autoplan-hour">
            {[6,9,12,15,18,21].map((h) => (
              <option key={h} value={h}>{String(h).padStart(2,"0")}:00 IST</option>
            ))}
          </select>
        </span>
      </label>
      {msg && <div className="mt-2 text-[10px] text-emerald-700">{msg}</div>}
    </div>
  );
};


// ──────────────────────────────────────────────────────────────────────
// iter149 · Plan-tomorrow card
// ──────────────────────────────────────────────────────────────────────
const PlanTomorrowCard = ({ authAxios }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const run = async (force) => {
    setBusy(true); setError(""); setResult(null);
    try {
      const r = await authAxios.post(
        `${API_URL}/api/admin/doozyad/brain/plan-tomorrow?force=${force ? "true" : "false"}`,
        null, { timeout: 240000 },
      );
      setResult(r.data);
    } catch (e) { setError(e?.response?.data?.detail || "Plan failed"); }
    setBusy(false);
  };

  return (
    <div className="bg-white border border-violet-200 rounded-xl p-4" data-testid="doozyad-brain-plan-tomorrow">
      <h4 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-1.5 mb-2">
        <Calendar size={14} className="text-violet-600" /> Plan tomorrow's post
      </h4>
      <p className="text-[11px] text-[#64748B] mb-3">
        Brain decides → generates image → stamps schedule. If confidence ≥ your threshold, it auto-schedules.
        Otherwise it sits in Pending for your approval. Takes ~60 sec.
      </p>
      <div className="flex gap-2">
        <button onClick={() => run(false)} disabled={busy}
          className="flex-1 px-3 py-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-xs font-bold rounded-lg disabled:opacity-50 flex items-center justify-center gap-1.5"
          data-testid="doozyad-brain-plan-btn">
          {busy ? <><Loader2 size={12} className="animate-spin" /> Planning…</> : <><Sparkles size={12} /> Plan now</>}
        </button>
        <button onClick={() => run(true)} disabled={busy}
          className="px-3 py-2 bg-white border border-violet-200 text-violet-700 text-xs font-semibold rounded-lg disabled:opacity-50"
          data-testid="doozyad-brain-plan-force-btn">
          Force re-plan
        </button>
      </div>
      {result && (
        <div className={`mt-3 p-2 rounded text-[11px] ${result.status === "auto_scheduled" ? "bg-emerald-50 border border-emerald-200" : result.status === "already_planned_today" ? "bg-amber-50 border border-amber-200" : "bg-violet-50 border border-violet-200"}`}>
          <div className="font-semibold text-[#1E1B4B] mb-0.5">
            {result.status === "auto_scheduled" && "✅ Auto-scheduled"}
            {result.status === "pending_approval" && "⏳ Pending approval"}
            {result.status === "already_planned_today" && "ℹ️ Already planned today — use Force re-plan"}
          </div>
          {result.draft && (
            <div className="text-[10px] text-[#64748B]">
              Theme: <b>{result.draft.theme_id}</b> · Confidence {Math.round((result.confidence || 0) * 100)}%
              {result.scheduled_for_utc && <> · Publishes {new Date(result.scheduled_for_utc).toLocaleString("en-IN")}</>}
            </div>
          )}
        </div>
      )}
      {error && <div className="mt-2 text-[10px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{error}</div>}
    </div>
  );
};


// ──────────────────────────────────────────────────────────────────────
// iter149 · Ask About (natural-language Q&A with citations)
// ──────────────────────────────────────────────────────────────────────
const AskAboutCard = ({ authAxios }) => {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState(null);
  const [busy, setBusy] = useState(false);

  const ask = async () => {
    if (!question.trim()) return;
    setBusy(true); setAnswer(null);
    try {
      const r = await authAxios.post(`${API_URL}/api/admin/doozyad/brain/ask-about`,
        { question: question.trim() }, { timeout: 60000 });
      setAnswer(r.data);
    } catch (e) {
      setAnswer({ answer: `Error: ${e?.response?.data?.detail || "Q&A failed"}`, kb_citations: [], decision_citations: [] });
    }
    setBusy(false);
  };

  return (
    <div className="bg-white border border-violet-200 rounded-xl p-4" data-testid="doozyad-brain-ask-about">
      <h4 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-1.5 mb-2">
        <MessageSquare size={14} className="text-violet-600" /> Ask the Brain anything
      </h4>
      <p className="text-[10px] text-[#64748B] mb-2">Q&amp;A mode. Cite sources from KB + prior decisions.</p>
      <textarea value={question} onChange={(e) => setQuestion(e.target.value)} rows={2}
        placeholder={"e.g. Which theme gives the best engagement rate this month?"}
        className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg outline-none focus:border-violet-400"
        data-testid="doozyad-brain-qa-input" />
      <button onClick={ask} disabled={busy || !question.trim()}
        className="mt-2 w-full px-3 py-1.5 bg-violet-600 text-white text-xs font-semibold rounded-lg disabled:opacity-50 flex items-center justify-center gap-1.5"
        data-testid="doozyad-brain-qa-btn">
        {busy ? <><Loader2 size={11} className="animate-spin" /> Thinking…</> : <><Send size={11} /> Ask</>}
      </button>
      {answer && (
        <div className="mt-3 p-2 bg-violet-50 border border-violet-200 rounded text-[11px] text-[#1E1B4B] whitespace-pre-line" data-testid="doozyad-brain-qa-answer">
          {answer.answer}
          {(answer.kb_citations?.length > 0 || answer.decision_citations?.length > 0) && (
            <div className="mt-2 pt-2 border-t border-violet-200 text-[9px] text-[#64748B]">
              {answer.kb_citations?.length > 0 && <span>📚 {answer.kb_citations.length} KB · </span>}
              {answer.decision_citations?.length > 0 && <span>🧠 {answer.decision_citations.length} prior decisions</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};


// ──────────────────────────────────────────────────────────────────────
// iter149 · Multi-channel status (YouTube/Twitter/GA4 creds_missing gate)
// ──────────────────────────────────────────────────────────────────────
const ChannelsStatusCard = ({ authAxios }) => {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancel = false;
    authAxios.get(`${API_URL}/api/admin/doozyad/channels/status`)
      .then((r) => { if (!cancel) setStatus(r.data); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [authAxios]);

  if (!status) return null;
  const rows = [
    { key: "youtube", label: "YouTube Shorts", icon: Youtube, cfg: status.youtube },
    { key: "twitter", label: "X / Twitter", icon: Twitter, cfg: status.twitter },
    { key: "ga4", label: "Google Analytics 4", icon: BarChart3, cfg: status.ga4 },
  ];
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="doozyad-brain-channels">
      <h4 className="text-sm font-bold text-[#1E1B4B] mb-2">Channels</h4>
      <div className="space-y-1.5">
        {rows.map((r) => {
          const Icon = r.icon;
          const ok = r.cfg?.fully_configured;
          return (
            <div key={r.key} className="flex items-center justify-between text-[11px]" data-testid={`doozyad-brain-channel-${r.key}`}>
              <span className="flex items-center gap-1.5 text-[#1E1B4B]">
                <Icon size={12} /> {r.label}
              </span>
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${ok ? "bg-emerald-100 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                {ok ? "Live" : "Creds needed"}
              </span>
            </div>
          );
        })}
      </div>
      <p className="text-[9px] text-[#94a3b8] mt-2">Admin → Setup guide returns the exact env keys for each channel.</p>
    </div>
  );
};

void CheckCircle2; void Zap;
